<template>
  <div class="reservation-calendar">
    <div class="reservation-calendar__toolbar">
      <a class="btn" :class="{ 'is-disabled': !canPrev }" @click="prev">前へ</a>
      <div class="reservation-calendar__toolbar-title" v-text="calendarTitle">
        ---------- ~ ----------
      </div>
      <a class="btn" @click="next">次へ</a>
    </div>

    <div v-if="isManuallyCreated" class="grey-text text-darken-1">
      ※開始時間が過ぎている内見の許諾・事前メールは送信されません
    </div>

    <div class="reservation-calendar__hidden-input">
      <input name="viewing[start_at]" :value="startAt" />
      <input name="viewing[end_at]" :value="endAt" />
      <input name="first_date" :value="firstDateStr" />
    </div>

    <table>
      <thead class="align-center">
        <tr>
          <th />
          <th v-for="wday in header" :key="wday" v-text="wday" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, rowIndex) in schedules" :key="rowIndex">
          <td class="reservation-calendar__time-label" v-text="row[0].time" />
          <td v-for="(schedule, colIndex) in row" :key="colIndex">
            <div
              class="reservation-calendar__cell"
              :class="cellClass(schedule)"
              @click="selectTime(schedule)"
              v-text="schedule.status"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { parseDate, addDuration, formatDate } from '@/utilities/date'

const DEFAULT_SELECT_RANGE = 7

export default {
  props: {
    propertyId: {
      type: [String, Number],
      required: true,
    },
    allday: {
      type: Boolean,
      default: false,
    },
    selectedStartAt: {
      type: String,
      default: null,
    },
    selectedEndAt: {
      type: String,
      default: null,
    },
    initialFirstDate: {
      type: String,
      default: null,
    },
    ignoreUnavailable: {
      type: Boolean,
      defualt: false,
    },
    isManuallyCreated: {
      type: Boolean,
      defualt: false,
    },
  },

  data() {
    return {
      maxContinuousReservations: 1,
      selectedTimes: [],
      header: [],
      schedules: [[{}]],
      dateRange: DEFAULT_SELECT_RANGE,
      firstDateStr: formatDate(this.initialFirstDate || new Date(), 'date'),
    }
  },

  computed: {
    startAt() {
      const startAt = this.selectedTimes.length
        ? this.selectedTimes[0]
        : ''
      this.$emit('update:selected-start-at', startAt)
      return startAt
    },
    endAt() {
      const endAt = this.selectedTimes.length
        ? formatDate(this.selectedTimes.slice(-1)[0], 'datetime', '30 minutes')
        : ''
      this.$emit('update:selected-end-at', endAt)
      return endAt
    },
    canPrev() {
      return this.firstDateStr > formatDate(new Date(), 'date') || this.isManuallyCreated
    },
    calendarTitle() {
      const firstDateStr = formatDate(this.firstDateStr, 'date')
      const lastDateStr = formatDate(this.firstDateStr, 'date', `${this.dateRange - 1} days`)
      return `${firstDateStr} 〜 ${lastDateStr}`
    },
  },

  async mounted() {
    await this.fetchReservationSchedules()
    this.setSelectedTimes()
  },

  methods: {
    async fetchReservationSchedules() {
      const path = `/properties/${this.propertyId}/viewing_schedules`
      const params = {
        date_range: this.dateRange,
        first_date: this.firstDateStr,
        allday: this.allday,
        selected_start_at: this.startAt,
        selected_end_at: this.endAt,
      }
      const {
        schedules,
        dateRange,
        header,
        max_continuous_reservations,
      } = await this.axios.get(path, { params })
      if (!schedules || !dateRange || !header || !max_continuous_reservations) return

      this.schedules = schedules
      this.dateRange = parseInt(dateRange)
      this.header = header
      this.maxContinuousReservations = max_continuous_reservations
    },
    setSelectedTimes() {
      this.selectedTimes = []
      let selectedStartAt = parseDate(this.selectedStartAt)
      const selectedEndAt = parseDate(this.selectedEndAt)
      const selectedStartAtStr = formatDate(selectedStartAt, 'date')

      // 選択期間が過去の場合はクリアする
      if (selectedStartAtStr < this.firstDateStr) {
        return
      }

      const [hour, minute] = this.schedules[0][0].time.split(':')
      const timeIndexOffset = hour * 2 + minute / 30
      const firstDate = parseDate(`${this.firstDateStr} 00:00`)

      while (selectedStartAt < selectedEndAt) {
        const timeIndex = selectedStartAt.getHours() * 2 + selectedStartAt.getMinutes() / 30 - timeIndexOffset
        const di = selectedStartAt.getTime() - firstDate.getTime()
        const dateIndex = Math.floor(di / (24 * 60 * 60 * 1000))
        this.selectTime(this.schedules[timeIndex][dateIndex])
        selectedStartAt = addDuration(selectedStartAt, '30 minutes')
      }
    },
    cellClass(schedule) {
      const classes = [`reservation-calendar__cell--${schedule.content}`]
      const dateTime = `${schedule.date} ${schedule.time}`
      if (this.isSelected(dateTime)) {
        classes.push('selected')
      }
      if (this.ignoreUnavailable && schedule.content === 'unavailable') {
        classes.push('ignore-unavailable')
      }
      return classes
    },
    prev() {
      if (!this.canPrev) return

      const today = formatDate(new Date(), 'date')
      const prev = formatDate(this.firstDateStr, 'date', `-${this.dateRange} days`)
      this.firstDateStr = prev < today && !this.isManuallyCreated ? today : prev
      this.fetchReservationSchedules()
      this.selectedTimes = []
    },
    next() {
      this.firstDateStr = formatDate(this.firstDateStr, 'date', `${this.dateRange} days`)
      this.fetchReservationSchedules()
      this.selectedTimes = []
    },
    selectTime(schedule) {
      if (schedule.status === '×' && !this.ignoreUnavailable) return

      const dateStr = `${schedule.date} ${schedule.time}`
      
      // 未選択の場合選択可能
      if (this.selectedTimes.length === 0) {
        this.selectedTimes.push(dateStr)
        return
      }

      // 選択済みの枠をクリックした時、端っこの場合は未選択にする
      if (this.isSelected(dateStr)) {
        const index = this.selectedTimes.indexOf(dateStr)
        if (index === 0 || index === this.selectedTimes.length - 1) {
          this.selectedTimes.splice(index, 1)
        }
        return
      }
      
      // 選択枠が埋まっている場合は選択できない
      if (this.selectedTimes.length >= this.maxContinuousReservations) {
        return
      }

      // 既存で選択中の枠がある場合、その前後のみ選択可能
      const prevDateStr = formatDate(this.selectedTimes[0], 'datetime', '-30 minutes')
      if (dateStr === prevDateStr) {
        this.selectedTimes.unshift(dateStr)
      }
      
      const nextDateStr = formatDate(this.selectedTimes.slice(-1)[0], 'datetime', '30 minutes')
      if (dateStr === nextDateStr) {
        this.selectedTimes.push(dateStr)
      }
    },
    isSelected(dateTime) {
      return this.selectedTimes.includes(dateTime)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "materialize-css/sass/components/_color-variables";
@import "materialize-css/sass/components/_variables.scss";

.reservation-calendar {
  max-width: 800px;
  width: 100%;

  .btn.is-disabled {
    cursor: not-allowed;
    background-color: map-get($grey, "base");
  }

  table {
    border-collapse: separate;

    th {
      border: solid #bfb9b0 1px;
      text-align: center;
      position: sticky;
      top: 0;
      background-color: #fff;
      border-bottom: solid 1px black;
    }

    td {
      border: solid #bfb9b0 1px;
      text-align: center;
      padding: 0 0;
    }
  }

  &__toolbar {
    display: flex;
    font-size: 22px;
    justify-content: space-evenly;
    margin-bottom: 14px;
  }

  &__toolbar-title {
    margin: auto;
  }

  &__time-label {
    background-color: #f6f6f6;
  }

  &__hidden-input {
    display: none;
  }

  &__cell {
    padding: 10px 5px;

    &--available {
      background-color: #fff;
      color: #00f;
      cursor: pointer;
      font-size: 1.3rem;

      &:hover {
        background-color: #fafafa;
      }

      &.selected {
        background-color: #fcdbe3;
      }
    }

    &--unavailable {
      background-color: #eee;
      color: #999;
      font-size: 1.3rem;

      &.ignore-unavailable {
        cursor: pointer;

        &:hover {
          background-color: #fafafa;
        }

        &.selected {
          background-color: #fcdbe3;
        }
      }
    }

    &--triangle {
      background-color: #fff;
      color: #896958;
      cursor: pointer;
      font-size: 1.3rem;

      &:hover {
        background-color: #fafafa;
      }

      &.selected {
        background-color: #fcdbe3;
      }
    }
  }
}

@media only screen and (max-width: 992px) {
  .reservation-calendar {
    &__toolbar {
      font-size: 15px;
    }
  }
}
</style>
